import statesByCountry from '@/data/states-by-country';
import {
  COUNTRY_CODE_WITH_NAMES,
  countryOptionsWithPriority,
} from '@/data/country-code-with-names';
import { COUNTRY_CODES } from '@/data/supported-country-codes';
import { Address } from '@clearbanc/data-common-types';
import { CountryCodeEntry } from '@/types/address.types';

const shortNameAttributes = ['country', 'administrative_area_level_1'];

export interface PlaceDataOutput {
  address_components: {
    longText: string;
    shortText: string;
    types: string[];
  }[];
}

interface SelectedState {
  label: string;
  value: string;
}

interface ParsedPlaceData extends Omit<Address, 'country' | 'state'> {
  country?: CountryCodeEntry;
  state?: SelectedState;
}

export const getCountryName = (countryCode: string): string => {
  if (!countryCode) return '';
  return COUNTRY_CODE_WITH_NAMES[countryCode]();
};

export const formatAddress = (address: Address): string => {
  if (!address) {
    return '';
  }

  return [
    address.line1 && `${address.line1}, `,
    address.city && `${address.city}<br />`,
    address.state && `${address.state}, `,
    address.postalCode && `${address.postalCode}, `,
    address.country && getCountryName(address.country),
  ]
    .filter((t) => !!t)
    .join('')
    .trim();
};

export const parsePlaceData = (placeData: PlaceDataOutput): ParsedPlaceData => {
  // Convert the raw API data into something more useable
  // https://developers.google.com/maps/documentation/javascript/reference/place#AddressComponent
  const addressComponents: Record<string, string> =
    placeData.address_components.reduce(
      (acc, component) => ({
        ...acc,
        [component.types[0]]: shortNameAttributes.includes(component.types[0])
          ? component.shortText
          : component.longText,
      }),
      {},
    );

  // Populate our form fields using the API data

  const {
    neighborhood,
    sublocality,
    locality,
    subpremise,
    postal_code: postalCode,
    country: selectedCountryName,
    sublocality_level_1: localityLevel1,
    administrative_area_level_1: selectedStateName,
    administrative_area_level_2: adminLevel2,
    postal_town: postalTown,
    street_number: streetNumber = '',
    route = '',
  } = addressComponents;

  const streetRoute =
    streetNumber || route ? `${streetNumber} ${route}` : undefined;

  const countryStates = statesByCountry[selectedCountryName];
  const selectedState = countryStates?.find(
    (countryState) => countryState.value === selectedStateName,
  ) as SelectedState;
  const defaultState: SelectedState = {
    value: selectedStateName,
    label: selectedStateName,
  };

  const state = selectedStateName ? selectedState || defaultState : undefined;

  const country = countryOptionsWithPriority([
    COUNTRY_CODES.US,
    COUNTRY_CODES.CA,
    COUNTRY_CODES.GB,
  ]).find((selectedCountry) => selectedCountry.value === selectedCountryName);

  return {
    line1:
      streetRoute || neighborhood || sublocality || localityLevel1 || locality,
    ...(subpremise && { line2: `#${subpremise}` }),
    country,
    state,
    city: postalTown || locality || adminLevel2,
    postalCode,
  };
};
